import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

// Business scope options
const BUSINESS_SCOPES = [
  { id: 'local', labelKey: 'local', descriptionKey: 'localDesc', icon: '📍' },
  { id: 'national', labelKey: 'national', descriptionKey: 'nationalDesc', icon: '🏠' },
  { id: 'international', labelKey: 'international', descriptionKey: 'internationalDesc', icon: '🌍' },
]

// Target markets (only for international)
const TARGET_MARKETS = [
  { id: 'market_dach', labelKey: 'dach' },
  { id: 'market_europe', labelKey: 'europe' },
  { id: 'market_northamerica', labelKey: 'northamerica' },
  { id: 'market_uk', labelKey: 'uk' },
  { id: 'market_asia', labelKey: 'asia' },
  { id: 'market_latam', labelKey: 'latam' },
  { id: 'market_mena', labelKey: 'mena' },
  { id: 'market_africa', labelKey: 'africa' },
  { id: 'market_worldwide', labelKey: 'worldwide' },
]

// Cultural preferences - internationalized for all languages
const CULTURAL_PREFERENCES = [
  // Tone & Formality
  { id: 'culture_formal', labelKey: 'formal', group: 'tone' },
  { id: 'culture_informal', labelKey: 'informal', group: 'tone' },
  // Style
  { id: 'culture_professional', labelKey: 'professional', group: 'style' },
  { id: 'culture_conversational', labelKey: 'conversational', group: 'style' },
  { id: 'culture_friendly', labelKey: 'friendly', group: 'style' },
  { id: 'culture_direct', labelKey: 'direct', group: 'style' },
  // Expressions
  { id: 'culture_local_idioms', labelKey: 'localIdioms', group: 'expressions' },
  { id: 'culture_international', labelKey: 'internationalStyle', group: 'expressions' },
  { id: 'culture_use_slang', labelKey: 'useSlang', group: 'expressions' },
  { id: 'culture_keep_simple', labelKey: 'keepSimple', group: 'expressions' },
  // Feel
  { id: 'culture_humorous', labelKey: 'humorous', group: 'feel' },
  { id: 'culture_serious', labelKey: 'serious', group: 'feel' },
  { id: 'culture_inspirational', labelKey: 'inspirational', group: 'feel' },
  { id: 'culture_educational', labelKey: 'educational', group: 'feel' },
]

/**
 * Business Scope Card Component
 */
function ScopeCard({ scope, isSelected, onSelect, t }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(scope.id)}
      className={`flex-1 p-4 rounded-lg border-2 text-left transition-all ${
        isSelected
          ? 'border-blue-600 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="text-2xl mb-2">{scope.icon}</div>
      <div className={`font-medium ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
        {t(`wizard.steps.geographic_context.businessScope.options.${scope.labelKey}`)}
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {t(`wizard.steps.geographic_context.businessScope.options.${scope.descriptionKey}`)}
      </div>
    </button>
  )
}

/**
 * Multi-Select Chip Component
 */
function ChipSelect({ options, selected, onChange, translationPrefix, t }) {
  const toggleOption = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id))
    } else {
      onChange([...selected, id])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.id)
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleOption(option.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t(`${translationPrefix}.${option.labelKey}`)}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Geographic Context Step Component
 *
 * Step 4 of the Brand Profile Wizard.
 * Collects: Business scope, location, target markets, cultural preferences
 */
export default function GeographicContextStep({ data, onChange }) {
  const { t } = useTranslation('brandProfile')

  // Handle business scope change
  const handleScopeChange = useCallback(
    (business_scope) => {
      onChange({
        business_scope,
        // Reset markets if not international
        target_markets: business_scope === 'international' ? data.target_markets : [],
      })
    },
    [onChange, data.target_markets]
  )

  // Handle primary location change
  const handleLocationChange = useCallback(
    (e) => {
      onChange({ primary_location: e.target.value })
    },
    [onChange]
  )

  // Handle target markets change
  const handleMarketsChange = useCallback(
    (target_markets) => {
      onChange({ target_markets })
    },
    [onChange]
  )

  // Handle cultural preferences change
  const handleCulturalChange = useCallback(
    (cultural_preferences) => {
      onChange({ cultural_preferences })
    },
    [onChange]
  )

  // Handle cultural notes change
  const handleNotesChange = useCallback(
    (e) => {
      onChange({ cultural_notes: e.target.value.slice(0, 150) })
    },
    [onChange]
  )

  // Get location placeholder based on scope
  const getLocationPlaceholder = () => {
    switch (data.business_scope) {
      case 'local':
        return t('wizard.steps.geographic_context.location.placeholderLocal')
      case 'national':
        return t('wizard.steps.geographic_context.location.placeholderNational')
      case 'international':
        return t('wizard.steps.geographic_context.location.placeholderInternational')
      default:
        return t('wizard.steps.geographic_context.location.placeholder')
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('wizard.steps.geographic_context.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('wizard.steps.geographic_context.subtitle')}
        </p>
      </div>

      {/* Business Scope */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t('wizard.steps.geographic_context.businessScope.label')}
        </label>
        <div className="flex gap-3">
          {BUSINESS_SCOPES.map((scope) => (
            <ScopeCard
              key={scope.id}
              scope={scope}
              isSelected={data.business_scope === scope.id}
              onSelect={handleScopeChange}
              t={t}
            />
          ))}
        </div>
      </div>

      {/* Primary Location - shown when scope is selected */}
      {data.business_scope && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('wizard.steps.geographic_context.location.label')}
          </label>
          <input
            type="text"
            value={data.primary_location || ''}
            onChange={handleLocationChange}
            placeholder={getLocationPlaceholder()}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Target Markets - only for international */}
      {data.business_scope === 'international' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('wizard.steps.geographic_context.targetMarkets.label')}
          </label>
          <ChipSelect
            options={TARGET_MARKETS}
            selected={data.target_markets}
            onChange={handleMarketsChange}
            translationPrefix="wizard.steps.geographic_context.targetMarkets.options"
            t={t}
          />
        </div>
      )}

      {/* Cultural Preferences */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('wizard.steps.geographic_context.culturalPreferences.label')}
          <span className="ml-2 text-gray-400 font-normal">({t('common.optional')})</span>
        </label>
        <p className="text-xs text-gray-500 mb-3">
          {t('wizard.steps.geographic_context.culturalPreferences.hint')}
        </p>
        <ChipSelect
          options={CULTURAL_PREFERENCES}
          selected={data.cultural_preferences}
          onChange={handleCulturalChange}
          translationPrefix="wizard.steps.geographic_context.culturalPreferences.options"
          t={t}
        />

        {/* Cultural Notes */}
        <div className="mt-4">
          <textarea
            value={data.cultural_notes || ''}
            onChange={handleNotesChange}
            placeholder={t('wizard.steps.geographic_context.culturalNotes.placeholder')}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
          <div className="mt-1 text-xs text-gray-400 text-right">
            {(data.cultural_notes || '').length}/150
          </div>
        </div>
      </div>
    </div>
  )
}
